import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { userService, postService, commentService, likeService, chatService, realtimeService, elogioService } from "./services/supabaseService";
import ChatToast from "./components/ChatToast";
import DatabaseDebug from "./components/DatabaseDebug";
import Toast from "./components/Toast";
import ConfirmModal from "./components/ConfirmModal";
import AdminPanel from "./AdminPanel";
import "./style.css";

const DEFAULT_AVATAR = "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg";

// Banco de frases motivacionais
const FRASES_MOTIVACIONAIS = [
  "Acredite em você e tudo será possível!",
  "O sucesso é a soma de pequenos esforços repetidos dia após dia.",
  "Seja a mudança que você deseja ver no mundo.",
  "Grandes coisas nunca vêm da zona de conforto.",
  "Você é mais forte do que imagina!",
  "Cada dia é uma nova oportunidade para ser feliz.",
  "Sonhe grande, trabalhe duro, mantenha o foco.",
  "A persistência é o caminho do êxito.",
  "Acredite nos seus sonhos e eles se tornarão realidade.",
  "O único limite é aquele que você coloca em si mesmo.",
  "Você nasceu para brilhar!",
  "Faça acontecer! Você tem o poder.",
  "Transforme seus obstáculos em oportunidades.",
  "Seja gentil, seja corajoso, seja você mesmo.",
  "O melhor ainda está por vir!",
  "Sua atitude determina sua direção.",
  "Não espere por oportunidades, crie-as!",
  "Você é capaz de coisas incríveis!",
  "A vida recompensa a ação.",
  "Mantenha a calma e siga em frente."
];

function Home({ user, onNav, onInitialLoadComplete, showInitialLoading }) {
  const [profileData, setProfileData] = useState(null);
  const [recado, setRecado] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [showComments, setShowComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [showAllFriends, setShowAllFriends] = useState(false);
  const [friendSearch, setFriendSearch] = useState("");
  const [topSearch, setTopSearch] = useState("");
  const [showLuckyDay, setShowLuckyDay] = useState(false);
  const [luckyPhrase, setLuckyPhrase] = useState("");
  const [mobileMenuExpanded, setMobileMenuExpanded] = useState(false);
  const [showMobilePublish, setShowMobilePublish] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [connectionRequest, setConnectionRequest] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [activeChatToast, setActiveChatToast] = useState(null);
  const [onlineUniversityUsers, setOnlineUniversityUsers] = useState(0);
  const [onlineUniversityUsersList, setOnlineUniversityUsersList] = useState([]);
  const [contentFilter, setContentFilter] = useState('all'); // all, friends, photos, videos, myPosts
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [userLikes, setUserLikes] = useState({}); // Rastrear quais posts o usuário curtiu
  const [likeAnimations, setLikeAnimations] = useState({}); // Animações de like
  const [toast, setToast] = useState(null); // { message, type, photo }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [unreadMessages, setUnreadMessages] = useState([]); // Mensagens não lidas
  const [messageNotification, setMessageNotification] = useState(null); // Notificação de mensagem
  const [searchedPosts, setSearchedPosts] = useState([]); // Posts encontrados na busca
  const [isSearchingPosts, setIsSearchingPosts] = useState(false); // Estado de busca de posts
  const [showAdminPanel, setShowAdminPanel] = useState(false); // Painel administrativo
  const [universitiesMap, setUniversitiesMap] = useState({}); // Mapa de universidades (nome -> cor)
  
  const showToast = (message, type = 'info', photo = null) => {
    setToast({ message, type, photo });
  };

  const showConfirm = (title, message, onConfirm, confirmType = 'danger') => {
    return new Promise((resolve) => {
      setConfirmModal({
        title,
        message,
        confirmType,
        onConfirm: () => {
          setConfirmModal(null);
          resolve(true);
          if (onConfirm) onConfirm();
        },
        onCancel: () => {
          setConfirmModal(null);
          resolve(false);
        }
      });
    });
  };

  // Função para abrir chat toast
  const openChatToast = (friendData) => {
    setActiveChatToast(friendData);
  };

  // Função para fechar chat toast
  const closeChatToast = () => {
    setActiveChatToast(null);
  };

  // Listener para mensagens do UserProfile
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'openChat') {
        openChatToast(event.data.userData);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Carregar universidades e suas cores
  useEffect(() => {
    const loadUniversities = async () => {
      try {
        const { data } = await supabase
          .from('universidades')
          .select('nome, sigla, cor_tag');
        
        if (data) {
          const map = {};
          data.forEach(uni => {
            const fullName = `${uni.nome} (${uni.sigla})`;
            map[fullName] = uni.cor_tag || '#667eea';
          });
          setUniversitiesMap(map);
        }
      } catch (error) {
      }
    };

    loadUniversities();
  }, []);

  // Calcula usuários online da mesma universidade
  useEffect(() => {
    const calculateOnlineUniversityUsers = async () => {
      if (!profileData?.universidade || !user?.id) return;

      try {
        // Busca todos os usuários da mesma universidade
        const { data: users, error } = await supabase
          .from('users')
          .select('id, nome, avatar, last_seen, universidade')
          .eq('universidade', profileData.universidade)
          .neq('id', user.id) // Exclui o próprio usuário
          .order('last_seen', { ascending: false });

        if (error) {
          return;
        }

        if (!users) {
          setOnlineUniversityUsers(0);
          setOnlineUniversityUsersList([]);
          return;
        }

        // Considera online se last_seen foi nos últimos 5 minutos
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const onlineUsers = users.filter(u => {
          if (!u.last_seen) return false;
          const lastSeen = new Date(u.last_seen);
          return lastSeen > fiveMinutesAgo;
        });

        setOnlineUniversityUsers(onlineUsers.length);
        setOnlineUniversityUsersList(onlineUsers.slice(0, 3)); // Pega os 3 primeiros
      } catch (error) {
      }
    };

    calculateOnlineUniversityUsers();
    // Recalcula a cada 30 segundos
    const interval = setInterval(calculateOnlineUniversityUsers, 30 * 1000);
    
    return () => clearInterval(interval);
  }, [profileData, user]);

  // Atualizar last_seen periodicamente (a cada 2 minutos)
  useEffect(() => {
    if (!user?.id) return;

    const updatePresence = async () => {
      try {
        await supabase
          .from('users')
          .update({ last_seen: new Date().toISOString() })
          .eq('id', user.id);
      } catch (error) {
      }
    };

    // Atualizar a cada 2 minutos
    const interval = setInterval(updatePresence, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.id]);

  // Listener para mensagens não lidas em tempo real
  useEffect(() => {
    if (!user?.id) return;

    // Buscar mensagens não lidas inicial
    const fetchUnreadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .or(`chat_id.like.%${user.id}%`)
          .eq('read', false)
          .neq('sender_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        if (data && data.length > 0) {
          setUnreadMessages(data);
        }
      } catch (error) {
      }
    };

    fetchUnreadMessages();

    // Subscrever a novas mensagens
    const channel = supabase
      .channel('user-messages')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, async (payload) => {
        const newMsg = payload.new;
        // Verificar se a mensagem é para o usuário atual
        const chatId = newMsg.chat_id;
        if (chatId && chatId.includes(user.id) && newMsg.sender_id !== user.id) {
          // Verificar se o chat está aberto
          const isChatOpen = activeChatToast?.userId === newMsg.sender_id;
          // Buscar dados do remetente
          const { data: sender } = await supabase
            .from('users')
            .select('*')
            .eq('id', newMsg.sender_id)
            .single();

          if (sender) {
            // Adicionar às mensagens não lidas
            setUnreadMessages(prev => [newMsg, ...prev]);

            // Mostrar notificação SOMENTE se o chat não estiver aberto
            if (!isChatOpen) {
              setMessageNotification({
                id: newMsg.id,
                message: newMsg.content,
                senderName: sender.nome,
                senderPhoto: sender.avatar || DEFAULT_AVATAR,
                senderData: { ...sender, userId: sender.id },
                timestamp: new Date(newMsg.created_at).getTime()
              });

              // Vibração
              if (navigator.vibrate) {
                navigator.vibrate([200, 100, 200]);
              }

              // Som de notificação (mais alto e realista)
              try {
                // Criar contexto de áudio
                const audioContext = new (window.AudioContext || window.webkitAudioContext)();
                
                // Criar oscilador para o som
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                // Configurar som (tipo notificação iOS/Android)
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Frequência mais alta
                
                // Envelope do som
                gainNode.gain.setValueAtTime(0, audioContext.currentTime);
                gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
                
                // Tocar
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.5);
                
              } catch (e) {
              }

              // Auto-fechar notificação após 5 segundos
              setTimeout(() => {
                setMessageNotification(null);
              }, 5000);
            }
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, activeChatToast]);



  const showConnectionRequest = (nearbyUser) => {
    setConnectionRequest(nearbyUser);
  };

  const handleConnectionResponse = (accepted) => {
    if (connectionRequest) {
      if (accepted) {
        // Navega para o perfil do usuário (viewProfile abre modal de outro usuário)
        onNav('viewProfile', connectionRequest.userId);
      }
      setConnectionRequest(null);
    }
  };

  useEffect(() => {
    if (user?.id) {
      initializeApp();
    }
  }, [user?.id]);
  
  const initializeApp = async () => {
    try {
      // Carregar dados
      loadProfile();
      loadPosts();
      loadFriends();
      loadAllUsers();
      checkLuckyDay();
      
    } catch (error) {
      showToast("Erro na inicialização: " + error.message, "error");
    }
  };

  // Hook para monitorar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkLuckyDay = () => {
    if (!user) return;
    
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const currentHour = new Date().getHours();
      
      // Buscar dados do localStorage
      const storedData = localStorage.getItem('luckyDay');
      let luckyData = storedData ? JSON.parse(storedData) : null;
      
      // Se não existe dados ou é um novo dia, sortear novo horário
      if (!luckyData || luckyData.date !== today) {
        const randomHour = Math.floor(Math.random() * 24); // 0-23
        const randomPhrase = FRASES_MOTIVACIONAIS[Math.floor(Math.random() * FRASES_MOTIVACIONAIS.length)];
        
        luckyData = {
          date: today,
          hour: randomHour,
          phrase: randomPhrase,
          shown: false
        };
        
        localStorage.setItem('luckyDay', JSON.stringify(luckyData));
      }
      
      // Verificar se estamos no horário sorteado e ainda não foi mostrado
      if (currentHour === luckyData.hour && !luckyData.shown) {
        setLuckyPhrase(luckyData.phrase);
        setShowLuckyDay(true);
        
        // Marcar como mostrado no localStorage
        luckyData.shown = true;
        localStorage.setItem('luckyDay', JSON.stringify(luckyData));
        
        // Contador removido (migração para Supabase)
      }
    } catch (err) {
    }
  };

  // Função removida - migração para Supabase

  const closeLuckyDay = () => {
    setShowLuckyDay(false);
  };

  const loadAllUsers = async () => {
    try {
      const usersData = await userService.getAllUsers();
      // Carrega lista de amigos para verificar status
      const friendIds = friends.map(friend => friend.id);
      const usersList = usersData
        .filter(userData => userData.id !== user?.id) // Exclui o próprio usuário
        .map(userData => {
          const userAvatar = userData.photo_url || userData.avatar || DEFAULT_AVATAR;
          return {
            id: userData.id,
            nome: userData.nome || userData.display_name || 'Usuário',
            avatar: userAvatar,
            universidade: userData.universidade || 'Universidade não informada',
            isFriend: friendIds.includes(userData.id)
          };
        });
      setAllUsers(usersList);
    } catch (error) {
    }
  };

  const handleSearchChange = async (value, isTopSearch = false) => {
    if (isTopSearch) {
      setTopSearch(value);
    } else {
      setFriendSearch(value);
    }
    
    if (value.trim().length >= 2) {
      // Buscar usuários
      let sourceList;
      if (isTopSearch) {
        // Busca do topo: todos os usuários
        sourceList = allUsers;
      } else {
        // Busca do painel: apenas amigos
        sourceList = friends.map(friend => ({
          id: friend.id,
          nome: friend.nome || 'Amigo',
          avatar: friend.avatar || DEFAULT_AVATAR,
          universidade: friend.universidade || 'Universidade não informada',
          isFriend: true
        }));
      }
      
      const filtered = sourceList.filter(user =>
        user.nome.toLowerCase().includes(value.toLowerCase()) ||
        (user.universidade || '').toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8); // Limita a 8 resultados
      
      setSearchResults(filtered);
      setShowSearchDropdown(true);
      
      // Buscar posts (apenas na busca do topo)
      if (isTopSearch && value.trim().length >= 3) {
        setIsSearchingPosts(true);
        try {
          // Buscar em posts recentes
          const recentPosts = posts.filter(post =>
            post.text?.toLowerCase().includes(value.toLowerCase()) ||
            post.authorName?.toLowerCase().includes(value.toLowerCase())
          );
          
          // Buscar em posts arquivados
          const archivedPosts = await postService.searchArchivedPosts(value.trim());
          
          // Combinar resultados
          const allFoundPosts = [...recentPosts, ...archivedPosts];
          
          // Remover duplicatas
          const uniquePosts = allFoundPosts.filter((post, index, self) => 
            index === self.findIndex(p => p.id === post.id)
          );
          
          setSearchedPosts(uniquePosts);
          console.log('🔍 Total de posts encontrados:', uniquePosts.length, '(Recentes:', recentPosts.length, '+ Arquivados:', archivedPosts.length, ')');
        } catch (err) {
          setSearchedPosts([]);
        } finally {
          setIsSearchingPosts(false);
        }
      } else {
        setSearchedPosts([]);
      }
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchedPosts([]);
    }
  };

  const selectSearchResult = (selectedUser) => {
    setShowSearchDropdown(false);
    setTopSearch('');
    setFriendSearch('');
    // Se é o próprio usuário, vai para Profile, senão para UserProfile
    if (selectedUser.id === user?.id) {
      onNav('profile');
    } else {
      onNav('viewProfile', selectedUser.id);
    }
  };

  const loadProfile = async () => {
    if (!user) return;
    try {
      const userProfile = await userService.getUserById(user.id);
      if (userProfile) {
        // Buscar elogios do usuário
        const elogios = await elogioService.getUserElogios(user.id);
        // Adicionar elogios ao perfil
        setProfileData({
          ...userProfile,
          elogios: elogios
        });
        
        // Atualizar last_seen para marcar presença online
        try {
          await supabase
            .from('users')
            .update({ last_seen: new Date().toISOString() })
            .eq('id', user.id);
        } catch (updateErr) {
        }
      } else {
      }
    } catch (err) {
      // Não mostrar toast aqui, pois pode ser normal não ter perfil ainda
    }
  };

  // Função para criar post de exemplo (apenas para teste)
  const criarPostExemplo = async () => {
    try {
      const postExemplo = {
        user_id: user.id,
        conteudo: 'Este é um post de exemplo criado automaticamente! 🎉',
        foto: '',
        video: '',
        created_at: new Date().toISOString()
      };
      
      const resultado = await postService.createPost(postExemplo);
      // Recarregar posts
      await loadPosts();
      showToast("Post de exemplo criado!", "success");
    } catch (err) {
      showToast("Erro ao criar post de exemplo", "error");
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      
      const postsData = await postService.getAllPosts();
      // Remover duplicatas por ID
      const uniquePosts = postsData.filter((post, index, self) => 
        index === self.findIndex(p => p.id === post.id)
      );
      
      // Ordena posts apenas por data (mais recente primeiro)
      uniquePosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      // Buscar contagem de likes para cada post
      if (uniquePosts.length > 0) {
        const postIds = uniquePosts.map(p => p.id);
        const { data: likesData, error: likesError } = await supabase
          .from('likes')
          .select('post_id, user_id')
          .in('post_id', postIds);
        
        if (!likesError && likesData) {
          // Contar likes por post
          const likesCountByPost = {};
          const userLikesState = {};
          
          likesData.forEach(like => {
            // Contar total de likes do post
            likesCountByPost[like.post_id] = (likesCountByPost[like.post_id] || 0) + 1;
            
            // Verificar se o usuário atual curtiu
            if (user?.id && like.user_id === user.id) {
              userLikesState[like.post_id] = true;
            }
          });
          
          // Atualizar posts com contagem correta
          const postsWithLikes = uniquePosts.map(post => ({
            ...post,
            likes_count: likesCountByPost[post.id] || 0
          }));
          
          setPosts(postsWithLikes);
          
          // Definir likes do usuário
          if (user?.id) {
            // Inicializar todos os posts como não curtidos
            const allLikesState = {};
            uniquePosts.forEach(post => {
              allLikesState[post.id] = userLikesState[post.id] || false;
            });
            setUserLikes(allLikesState);
            console.log('❤️ Likes do usuário carregados:', Object.keys(userLikesState).length);
          }
        } else {
          setPosts(uniquePosts);
        }
      } else {
        setPosts(uniquePosts);
      }
      
    } catch (err) {
      showToast("Erro ao carregar posts: " + err.message, "error");
      setPosts([]); // Definir como array vazio em caso de erro
    } finally {
      setLoading(false);
      // Notificar que o carregamento inicial terminou
      if (onInitialLoadComplete) {
        onInitialLoadComplete();
      }
    }
  };

  const loadFriends = async () => {
    if (!user) return;
    try {
      const friendsData = await userService.getFriends(user.id);
      setFriends(friendsData);
    } catch (err) {
    }
  };

  const handlePostar = async () => {
    if (!recado.trim() && !photoFile && !videoUrl.trim()) {
      showToast("Escreva algo, adicione uma foto ou um vídeo!", "warning");
      return;
    }

    try {
      let photoBase64 = "";
      
      // Convert photo to Base64 if exists
      if (photoFile) {
        photoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(photoFile);
        });
      }

      // Extract YouTube video ID if URL provided
      let youtubeId = "";
      if (videoUrl.trim()) {
        const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = videoUrl.match(youtubeRegex);
        youtubeId = match ? match[1] : "";
        
        if (!youtubeId) {
          showToast("URL do YouTube inválida. Use um link válido do YouTube.", "warning");
          return;
        }
      }
      
      if (!user?.id) {
        showToast("Usuário não autenticado", "error");
        return;
      }
      
      const postData = {
        user_id: user.id,
        conteudo: recado,
        foto: photoBase64,
        video: youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : ""
      };
      
      const newPost = await postService.createPost(postData);
      // Limpar formulário
      setRecado("");
      setPhotoFile(null);
      setVideoUrl("");
      setShowMediaOptions(false);
      setShowMobilePublish(false);
      
      // Recarregar posts para mostrar o novo post
      await loadPosts();
    } catch (err) {
      showToast("Erro ao publicar. Tente novamente.", "error");
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Por favor, selecione uma imagem válida.", "warning");
      return;
    }

    // Ler imagem e abrir editor de corte
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageToCrop(event.target.result);
      setShowImageCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedImageBase64) => {
    // Converter base64 para File
    fetch(croppedImageBase64)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setCroppedImage(croppedImageBase64);
        setShowImageCropper(false);
        setImageToCrop(null);
      });
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setImageToCrop(null);
    // Limpar o input file
    const fileInput = document.getElementById('post-photo-input');
    if (fileInput) fileInput.value = '';
  };

  const handleLike = async (postId, currentLikes = 0) => {
    const userId = user.id;
    
    if (!userId) {
      showToast("Você precisa estar logado para curtir", "warning");
      return;
    }
    
    try {
      console.log('👍 handleLike:', { postId, userId });
      
      // Buscar likes para este post (tabela likes)
      const { data: likesData, error: fetchError } = await supabase
        .from('likes')
        .select('id, user_id')
        .eq('post_id', postId);
      
      if (fetchError) throw fetchError;
      
      // Verificar se o usuário já curtiu
      const userLike = likesData?.find(l => l.user_id === userId);
      const hasLiked = !!userLike;
      
      console.log('   hasLiked:', hasLiked, 'total likes:', likesData?.length || 0);
      
      // Animação imediata (feedback visual)
      setLikeAnimations(prev => ({ ...prev, [postId]: true }));
      setTimeout(() => {
        setLikeAnimations(prev => ({ ...prev, [postId]: false }));
      }, 600);

      if (hasLiked) {
        console.log('   ⬇️ Removendo like...');
        
        // Deletar o like
        const { error: deleteError } = await supabase
          .from('likes')
          .delete()
          .eq('id', userLike.id);
        
        if (deleteError) throw deleteError;
        
        // Atualizar UI
        const newLikesCount = Math.max(0, (likesData?.length || 1) - 1);
        setUserLikes(prev => ({ ...prev, [postId]: false }));
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, likes_count: newLikesCount }
            : post
        ));
        
        console.log('   ✅ Like removido');
      } else {
        console.log('   ⬆️ Adicionando like...');
        
        // Criar like (verifica duplicata no banco)
        const { error: insertError } = await supabase
          .from('likes')
          .insert([{
            post_id: postId,
            user_id: userId
          }]);
        
        if (insertError) throw insertError;
        
        // Atualizar UI
        const newLikesCount = (likesData?.length || 0) + 1;
        setUserLikes(prev => ({ ...prev, [postId]: true }));
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, likes_count: newLikesCount }
            : post
        ));
        
        console.log('   ✅ Like adicionado');
      }
      
    } catch (err) {
      showToast(`Erro ao curtir: ${err.message}`, "error");
      
      // Reverter mudanças otimistas em caso de erro
      try {
        // Recarregar posts SEM resetar o estado de likes atual
        const postsData = await postService.getPosts();
        const uniquePosts = Array.from(
          new Map(postsData.map(post => [post.id, post])).values()
        );
        
        // Atualizar apenas os posts, MANTENDO o estado de likes
        setPosts(uniquePosts);
        console.log('✅ Posts recarregados (likes mantidos)');
      } catch (loadErr) {
      }
    }
  };

  const handleComment = async (postId, commentText) => {
    if (!commentText?.trim()) {
      showToast("Escreva um comentário!", "warning");
      return;
    }

    try {
      const newComment = await commentService.addComment({
        post_id: postId,
        user_id: user.id,
        conteudo: commentText.trim(),
        created_at: new Date().toISOString()
      });

      // Atualizar o post localmente sem recarregar tudo
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          // post.comments é um objeto, não array
          const updatedComments = {
            ...(post.comments || {}),
            [newComment.id]: {
              text: newComment.conteudo,
              authorId: newComment.user_id,
              authorName: user.nome || profileData?.nome,
              authorPhoto: user.avatar || profileData?.avatar,
              authorVerified: user.verified || profileData?.verified || false,
              createdAt: newComment.created_at
            }
          };
          return {
            ...post,
            comments: updatedComments,
            comments_count: Object.keys(updatedComments).length
          };
        }
        return post;
      }));

      // Limpar campo de comentário
      setCommentText(prev => ({
        ...prev,
        [postId]: ""
      }));
    } catch (err) {
      showToast("Erro ao comentar", "error");
    }
  };

  const handleRemovePost = async (postId) => {
    const confirmed = await showConfirm(
      "Excluir Post",
      "Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.",
      null,
      "danger"
    );
    
    if (!confirmed) return;

    try {
      const success = await postService.deletePost(postId, user.id);
      
      if (success) {
        await loadPosts();
        showToast("Post excluído com sucesso!", "success");
      } else {
        showToast("Erro ao excluir post", "error");
      }
    } catch (err) {
      showToast("Erro ao excluir post", "error");
    }
  };

  const handleDeleteComment = async (postId, commentId, commentAuthorId, postAuthorId) => {
    // Pode deletar se:
    // 1. É o autor do comentário
    // 2. É o dono do post
    const canDelete = user.id === commentAuthorId || user.id === postAuthorId;
    
    if (!canDelete) {
      showToast("Você não tem permissão para excluir este comentário", "error");
      return;
    }

    const confirmed = await showConfirm(
      "Excluir Comentário",
      "Tem certeza que deseja excluir este comentário?",
      null,
      "danger"
    );
    
    if (!confirmed) return;

    try {
      await commentService.deleteComment(commentId);
      
      // Atualizar posts localmente removendo o comentário
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const updatedComments = { ...post.comments };
          delete updatedComments[commentId];
          return {
            ...post,
            comments: updatedComments,
            comments_count: Object.keys(updatedComments).length
          };
        }
        return post;
      }));

      showToast("Comentário excluído com sucesso!", "success");
    } catch (err) {
      showToast("Erro ao excluir comentário", "error");
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'agora mesmo';
    
    try {
      const now = new Date();
      const postDate = new Date(timestamp);
      
      // Verificar se a data é válida
      if (isNaN(postDate.getTime())) {
        return 'agora mesmo';
      }
      
      const diff = Math.floor((now - postDate) / 1000);
      
      if (diff < 0) return 'agora mesmo'; // Post no futuro
      if (diff < 60) return 'agora mesmo';
      if (diff < 3600) {
        const minutes = Math.floor(diff / 60);
        return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
      }
      if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
      }
      if (diff < 604800) { // 7 dias
        const days = Math.floor(diff / 86400);
        return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
      }
      
      // Para posts mais antigos, mostrar data
      return postDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
    } catch (error) {
      return 'agora mesmo';
    }
  };

  // Função para extrair sigla da universidade
  const getUniversityAcronym = (universityName) => {
    if (!universityName) return '';
    
    // Extrai o que está entre parênteses (ex: UNIVALI, USP, UFRJ)
    const match = universityName.match(/\(([^)]+)\)/);
    if (match) {
      return match[1];
    }
    
    // Se não tem parênteses, pega as primeiras letras das palavras principais
    const words = universityName.split(' ').filter(word => 
      word.length > 2 && 
      !['do', 'da', 'de', 'dos', 'das', 'para', 'com', 'por'].includes(word.toLowerCase())
    );
    
    return words.map(word => word.charAt(0)).join('').toUpperCase().substring(0, 6);
  };

  // Função para obter a cor da universidade
  const getUniversityColor = (universityName) => {
    if (!universityName) return "#667eea";
    
    // Busca no mapa de universidades do banco de dados
    if (universitiesMap[universityName]) {
      return universitiesMap[universityName];
    }
    
    // Cor padrão caso não encontre
    return "#667eea";
  };

  // Função para verificar se está em mobile (usa estado reativo)
  const isMobile = () => {
    return isMobileView;
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err) {
    }
  };

  // Mostrar tela de loading enquanto carrega o feed inicial
  if (showInitialLoading && loading) {
    return (
      <div className="orkut-loading-screen">
        <div className="orkut-loading-content">
          <h1 className="orkut-loading-title">toNetwork</h1>
          <div className="orkut-loading-text">Carregando feed...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`orkut-main-layout ${showMobileSearch ? 'search-active' : ''}`}>
      <div className="orkut-topbar">
        <div className="orkut-topbar-left">
          <a href="#" className="active">Home</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("profile"); }}>Perfil</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("startups"); }}>StartUps</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("revistas"); }}>Revista</a>
          <a href="#" onClick={(e) => { e.preventDefault(); onNav("friends"); }}>Amigos</a>
        </div>
        <div className="orkut-topbar-network">
          <div 
            className="orkut-topbar-left-content"
            onClick={() => {
              // Verificar se é admin
              if (user?.email === 'herick.8434158@edu.univali.br') {
                setShowAdminPanel(true);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <span className="orkut-network-text">toNetwork</span>
          </div>
          <div className="orkut-topbar-mobile-buttons">
            <button className="orkut-mobile-search-btn" onClick={() => setShowMobileSearch(!showMobileSearch)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </button>
            <button className="orkut-mobile-add-btn" onClick={() => setShowMobilePublish(!showMobilePublish)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Barra de Pesquisa Mobile - Aparece abaixo da topbar */}
        {showMobileSearch && (
          <div className="orkut-mobile-search-bar">
            <div className="orkut-mobile-search-bar-content">
              <button 
                className="orkut-mobile-search-close" 
                onClick={() => {
                  setShowMobileSearch(false);
                  setTopSearch('');
                  setSearchResults([]);
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <input
                type="text"
                placeholder="Buscar conexões..."
                className="orkut-mobile-search-bar-input"
                value={topSearch}
                onChange={(e) => handleSearchChange(e.target.value, true)}
                autoFocus
              />
            </div>
            {(searchResults.length > 0 || searchedPosts.length > 0 || isSearchingPosts) && (
              <div className="orkut-mobile-search-bar-results">
                {/* Seção de Usuários */}
                {searchResults.length > 0 && (
                  <>
                    <div className="search-section-title" style={{ padding: '8px 12px', background: '#f8f9ff', fontWeight: '600', color: '#2d5aa0', fontSize: '0.85em', borderBottom: '1px solid #e5eaf5' }}>
                      👥 Pessoas
                    </div>
                    {searchResults.map(user => (
                      <div
                        key={user.id}
                        className="orkut-mobile-search-bar-result"
                        onClick={() => {
                          selectSearchResult(user);
                          setShowMobileSearch(false);
                          setTopSearch('');
                          setSearchResults([]);
                          setSearchedPosts([]);
                        }}
                      >
                        <img 
                          src={user.avatar || DEFAULT_AVATAR} 
                          alt={user.nome}
                          className="orkut-mobile-search-bar-avatar"
                        />
                        <div className="orkut-mobile-search-bar-info">
                          <div className="orkut-mobile-search-bar-name">{user.nome}</div>
                          <div className="orkut-mobile-search-bar-university">{user.universidade || 'Universidade'}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                
                {/* Seção de Posts */}
                {searchedPosts.length > 0 && (
                  <>
                    <div className="search-section-title" style={{ padding: '8px 12px', background: '#f8f9ff', fontWeight: '600', color: '#2d5aa0', fontSize: '0.85em', borderBottom: '1px solid #e5eaf5', marginTop: '8px' }}>
                      📝 Posts encontrados ({searchedPosts.length})
                    </div>
                    {searchedPosts.slice(0, 5).map(post => (
                      <div
                        key={post.id}
                        className="orkut-mobile-search-bar-result"
                        style={{ alignItems: 'flex-start', padding: '12px' }}
                        onClick={() => {
                          setShowMobileSearch(false);
                          setTopSearch('');
                          setSearchResults([]);
                          setSearchedPosts([]);
                          
                          // Verificar se o post está no feed atual
                          const postElement = document.getElementById(`post-${post.id}`);
                          
                          if (postElement) {
                            // Post está no feed, só fazer scroll
                            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            postElement.style.animation = 'highlight 2s ease';
                          } else if (post.isArchived) {
                            // Post arquivado: adicionar temporariamente no topo do feed
                            console.log('📌 Adicionando post arquivado ao topo do feed (mobile)');
                            setPosts(prev => [post, ...prev]);
                            
                            // Scroll após adicionar
                            setTimeout(() => {
                              const newPostElement = document.getElementById(`post-${post.id}`);
                              if (newPostElement) {
                                newPostElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                newPostElement.style.animation = 'highlight 2s ease';
                              }
                            }, 100);
                            
                            showToast('📦 Post arquivado exibido temporariamente', 'info');
                          }
                        }}
                      >
                        <img 
                          src={post.authorPhoto || DEFAULT_AVATAR} 
                          alt={post.authorName}
                          className="orkut-mobile-search-bar-avatar"
                        />
                        <div className="orkut-mobile-search-bar-info" style={{ flex: 1 }}>
                          <div className="orkut-mobile-search-bar-name">
                            {post.authorName}
                            {post.isArchived && <span style={{ fontSize: '0.75em', color: '#999', marginLeft: '4px' }}>📦</span>}
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#333', marginTop: '2px' }}>
                            {post.text?.substring(0, 60)}
                            {post.text?.length > 60 && '...'}
                          </div>
                          <div style={{ fontSize: '0.75em', color: '#999', marginTop: '2px' }}>
                            {new Date(post.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        {post.postPhoto && (
                          <img 
                            src={post.postPhoto} 
                            alt="Post" 
                            style={{ width: '50px', height: '50px', borderRadius: '4px', objectFit: 'cover', marginLeft: '8px' }}
                          />
                        )}
                      </div>
                    ))}
                  </>
                )}
                
                {isSearchingPosts && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '0.9em' }}>
                    🔍 Buscando posts...
                  </div>
                )}
              </div>
            )}
            {topSearch.length >= 2 && searchResults.length === 0 && searchedPosts.length === 0 && !isSearchingPosts && (
              <div className="orkut-mobile-search-bar-empty">
                Nenhuma conexão encontrada
              </div>
            )}
          </div>
        )}

        <div className="orkut-topbar-right">
          <div className="search-container">
            <input
              type="text"
              placeholder="buscar conexões"
              className="orkut-search"
              value={topSearch}
              onChange={(e) => handleSearchChange(e.target.value, true)}
              onFocus={() => {
                if (topSearch.length >= 2) {
                  handleSearchChange(topSearch, true);
                }
              }}
              onBlur={() => {
                // Delay para permitir clique nos resultados
                setTimeout(() => setShowSearchDropdown(false), 300);
              }}
            />
            {showSearchDropdown && (searchResults.length > 0 || searchedPosts.length > 0) && (
              <div className="search-dropdown">
                {/* Seção de Usuários */}
                {searchResults.length > 0 && (
                  <div className="search-section">
                    <div className="search-section-title">👥 Pessoas</div>
                    {searchResults.map(user => {
                      return (
                        <div
                          key={user.id}
                          className="search-result-item"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectSearchResult(user);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault(); // Previne o blur antes do click
                          }}
                        >
                          <img
                            src={user.avatar}
                            alt={user.nome}
                            className="search-result-avatar"
                            onError={(e) => {
                              e.target.src = DEFAULT_AVATAR;
                            }}
                            onLoad={() => {
                            }}
                          />
                          <div className="search-result-info">
                            <div className="search-result-name">{user.nome}</div>
                            <div 
                              className="search-result-university"
                              style={{ color: getUniversityColor(user.universidade) }}
                            >
                              {getUniversityAcronym(user.universidade)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Seção de Posts */}
                {searchedPosts.length > 0 && (
                  <div className="search-section">
                    <div className="search-section-title">
                      📝 Posts encontrados ({searchedPosts.length})
                    </div>
                    {searchedPosts.slice(0, 5).map(post => (
                      <div
                        key={post.id}
                        className="search-post-item"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowSearchDropdown(false);
                          setTopSearch('');
                          
                          // Verificar se o post está no feed atual
                          const postElement = document.getElementById(`post-${post.id}`);
                          
                          if (postElement) {
                            // Post está no feed, só fazer scroll
                            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            postElement.style.animation = 'highlight 2s ease';
                          } else if (post.isArchived) {
                            // Post arquivado: adicionar temporariamente no topo do feed
                            setPosts(prev => [post, ...prev]);
                            
                            // Scroll após adicionar (delay para garantir renderização)
                            setTimeout(() => {
                              const newPostElement = document.getElementById(`post-${post.id}`);
                              if (newPostElement) {
                                newPostElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                newPostElement.style.animation = 'highlight 2s ease';
                              }
                            }, 100);
                            
                            showToast('📦 Post arquivado exibido temporariamente', 'info');
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                      >
                        <img
                          src={post.authorPhoto || DEFAULT_AVATAR}
                          alt={post.authorName}
                          className="search-post-avatar"
                        />
                        <div className="search-post-info">
                          <div className="search-post-author">
                            {post.authorName}
                            {post.isArchived && <span className="search-post-archived"> 📦 (arquivado)</span>}
                          </div>
                          <div className="search-post-text">
                            {post.text?.substring(0, 80)}
                            {post.text?.length > 80 && '...'}
                          </div>
                          <div className="search-post-date">
                            {new Date(post.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        {post.postPhoto && (
                          <img src={post.postPhoto} alt="Post" className="search-post-thumb" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {isSearchingPosts && (
                  <div className="search-loading">🔍 Buscando posts...</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barra de Filtros Mobile - apenas no mobile */}
      {isMobile() && (
        <div className="mobile-filter-bar">
          <button 
            className={`mobile-filter-btn ${contentFilter === 'all' ? 'active' : ''}`}
            onClick={() => setContentFilter('all')}
          >
            Todos
          </button>
          <button 
            className={`mobile-filter-btn ${contentFilter === 'friends' ? 'active' : ''}`}
            onClick={() => setContentFilter('friends')}
          >
            Posts de Amigos
          </button>
          <button 
            className={`mobile-filter-btn ${contentFilter === 'photos' ? 'active' : ''}`}
            onClick={() => setContentFilter('photos')}
          >
            Fotos
          </button>
          <button 
            className={`mobile-filter-btn ${contentFilter === 'videos' ? 'active' : ''}`}
            onClick={() => setContentFilter('videos')}
          >
            Vídeos
          </button>
          <button 
            className={`mobile-filter-btn ${contentFilter === 'myPosts' ? 'active' : ''}`}
            onClick={() => setContentFilter('myPosts')}
          >
            Meus
          </button>
        </div>
      )}

      <div className="orkut-content">
        <aside className="orkut-sidebar-left">
          <div 
            className="orkut-avatar"
            style={{
              backgroundImage: `url(${profileData?.avatar || DEFAULT_AVATAR})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="orkut-user-name">{profileData?.nome || user?.email}</div>
          <div className="orkut-invisible">● online</div>
          
          <div className="orkut-elogios-box">
            <div className="orkut-elogio-titulo">Elogios</div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">⭐ Legal</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.legal || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">💙 Confiável</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.confiavel || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">🎨 Criativo</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.criativo || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">🧠 Inteligente</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.inteligente || 0}</span>
            </div>
            <div className="orkut-elogio-item">
              <span className="orkut-elogio-label">😊 Simpático</span>
              <span className="orkut-elogio-nota">{profileData?.elogios?.simpatico || 0}</span>
            </div>
          </div>

          <div className="orkut-profile-links">
            <a href="#" onClick={(e) => { e.preventDefault(); onNav("profile"); }}>editar perfil</a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setContentFilter('all'); }}
              className={contentFilter === 'all' ? 'active' : ''}
            >
              todos
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setContentFilter('friends'); }}
              className={contentFilter === 'friends' ? 'active' : ''}
            >
              posts de amigos
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setContentFilter('photos'); }}
              className={contentFilter === 'photos' ? 'active' : ''}
            >
              fotos
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setContentFilter('videos'); }}
              className={contentFilter === 'videos' ? 'active' : ''}
            >
              vídeos
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); setContentFilter('myPosts'); }}
              className={contentFilter === 'myPosts' ? 'active' : ''}
            >
              meus posts
            </a>
            <a href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }} style={{color: '#d73527'}}>sair</a>
          </div>
          <div className="orkut-profile-info">
            <p><strong>Relacionamento:</strong> {profileData?.status || "solteiro(a)"}</p>
            <p><strong>Aniversário:</strong> {profileData?.aniversario || "não informado"}</p>
            <p><strong>Cidade:</strong> {profileData?.cidade || "Itajaí, SC"}</p>
          </div>
        </aside>

        <main className="orkut-center">
          <div className={`orkut-post-box ${showMobilePublish ? 'mobile-visible' : ''}`}>
            <textarea 
              placeholder="Clique para deixar um recado para seus amigos, postar uma foto, vídeo e mais..."
              value={recado}
              onChange={(e) => setRecado(e.target.value)}
              rows={3}
            />
            
            {photoFile && croppedImage && (
              <div className="orkut-selected-photo" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <img src={croppedImage} alt="Preview" style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '8px' }} />
                <button onClick={() => { setPhotoFile(null); setCroppedImage(null); }} style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer' }}>
                  Remover foto
                </button>
              </div>
            )}
            
            {videoUrl && (
              <div className="orkut-selected-video">
                🎥 Vídeo: {videoUrl.substring(0, 50)}...
                <button onClick={() => setVideoUrl("")}>❌</button>
              </div>
            )}
            
            <div className="orkut-post-actions">
              <button className="orkut-btn-primary" onClick={handlePostar}>postar</button>
              <button className="orkut-btn-secondary" onClick={() => { setRecado(""); setPhotoFile(null); setVideoUrl(""); setShowMobilePublish(false); }}>cancelar</button>
              <div className="orkut-post-icons">
                <input 
                  type="file" 
                  id="post-photo-input" 
                  accept="image/*" 
                  style={{display: 'none'}} 
                  onChange={handlePhotoSelect}
                />
                <span onClick={() => document.getElementById('post-photo-input').click()} style={{cursor: 'pointer'}}>📷</span>
                <span onClick={() => setShowMediaOptions(!showMediaOptions)} style={{cursor: 'pointer'}}>🎥</span>
              </div>
            </div>
            
            {showMediaOptions && (
              <div className="orkut-video-input">
                <input 
                  type="text" 
                  placeholder="Cole o link do YouTube aqui..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                />
                <button onClick={() => setShowMediaOptions(false)}>✓</button>
              </div>
            )}
          </div>

          <div className="orkut-updates">

            

            <div className="orkut-feed-section">
            {!isMobile() && (
              <div style={{ marginBottom: '10px' }}>
                <h3>Publicações da rede</h3>
              </div>
            )}
            {loading ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                Carregando publicações...
              </div>
            ) : posts.length === 0 ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                Ainda não há publicações. Seja o primeiro a publicar algo!
              </div>
            ) : (() => {
              const filteredPosts = posts.filter(post => {
                // Aplica filtros baseados na seleção do usuário
                if (contentFilter === 'all') return true;
                if (contentFilter === 'friends') {
                  // Mostra posts de amigos (incluindo posts próprios)
                  const friendsArray = Array.isArray(friends) ? friends : [];
                  return friendsArray.some(friend => friend.id === post.authorId) || post.authorId === user?.id;
                }
                if (contentFilter === 'myPosts') return post.authorId === user?.id;
                if (contentFilter === 'photos') return post.postPhoto && !post.videoUrl;
                if (contentFilter === 'videos') return post.videoUrl;
                return true;
              });

              if (filteredPosts.length === 0) {
                const filterMessages = {
                  'friends': 'Não há publicações de amigos para exibir.',
                  'myPosts': 'Você ainda não fez nenhuma publicação.',
                  'photos': 'Não há publicações com fotos para exibir.',
                  'videos': 'Não há publicações com vídeos para exibir.'
                };
                return (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                    {filterMessages[contentFilter] || 'Nenhuma publicação encontrada.'}
                  </div>
                );
              }

              return filteredPosts.map(post => (
                <div key={post.id} id={`post-${post.id}`} data-post-id={post.id} className="orkut-post-item">
                  <div 
                    className="orkut-post-avatar"
                    onClick={() => onNav("viewProfile", post.authorId)}
                    style={{
                      cursor: 'pointer',
                      backgroundImage: `url(${post.authorPhoto || DEFAULT_AVATAR})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  <div className="orkut-post-content">
                    <div className="orkut-post-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <strong 
                            className="orkut-post-author"
                            onClick={() => onNav("viewProfile", post.authorId)}
                            style={{ cursor: 'pointer' }}
                          >{post.authorName}</strong>
                          {post.authorVerified && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="#1DA1F2"
                              className="verified-badge"
                              title="Perfil Verificado"
                            >
                              <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
                            </svg>
                          )}
                          {post.isArchived && (
                            <span 
                              className="archived-post-badge"
                              title="Post arquivado - exibido temporariamente"
                            >
                              📦 Arquivado
                            </span>
                          )}
                        </div>
                        {post.authorUniversity && (
                          <span 
                            className="university-indicator"
                            style={{ 
                              color: getUniversityColor(post.authorUniversity),
                              fontWeight: '600'
                            }}
                            title={`Universidade: ${post.authorUniversity}`}
                          >
                             {isMobile() ? getUniversityAcronym(post.authorUniversity) : post.authorUniversity.split('(')[0].trim()}
                          </span>
                        )}
                      </div>
                      <span className="orkut-post-time">{formatTime(post.createdAt)}</span>
                    </div>
                    <div className="orkut-post-text">{post.text}</div>
                    
                    {post.postPhoto && (
                      <div className="orkut-post-photo">
                        <img src={post.postPhoto} alt="Foto da publicação" />
                      </div>
                    )}
                    
                    {post.videoUrl && (
                      <div className="orkut-post-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${post.videoUrl}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="YouTube video"
                        ></iframe>
                      </div>
                    )}
                    
                    <div className="orkut-post-actions-bar">
                      <div 
                        className={`orkut-post-action ${likeAnimations[post.id] ? 'like-animation' : ''}`}
                        onClick={() => handleLike(post.id, post.likes_count || 0)}
                        style={{ 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          transition: 'transform 0.2s ease, color 0.2s ease'
                        }}
                      >
                        {userLikes[post.id] ? (
                          <svg 
                            width="18" 
                            height="18" 
                            viewBox="0 0 24 24" 
                            fill="#ff4757" 
                            className="like-icon"
                            style={{ 
                              transform: likeAnimations[post.id] ? 'scale(1.3)' : 'scale(1)',
                              transition: 'transform 0.3s ease'
                            }}
                          >
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                          </svg>
                        ) : (
                          <svg 
                            width="18" 
                            height="18" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#666" 
                            strokeWidth="2" 
                            className="like-icon"
                            style={{ 
                              transform: likeAnimations[post.id] ? 'scale(1.2)' : 'scale(1)',
                              transition: 'transform 0.3s ease'
                            }}
                          >
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        )}
                        <span style={{ 
                          color: userLikes[post.id] ? '#ff4757' : '#666',
                          fontWeight: userLikes[post.id] ? 'bold' : 'normal',
                          transition: 'color 0.2s ease'
                        }}>
                          {post.likes_count || 0}
                        </span>
                      </div>
                      <div 
                        className="orkut-post-action"
                        onClick={() => setShowComments({ ...showComments, [post.id]: !showComments[post.id] })}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" className="comment-icon">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        {post.comments_count || 0}
                      </div>
                      {post.user_id === user.id && (
                        <button 
                          className="orkut-post-delete"
                          onClick={() => handleRemovePost(post.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" className="delete-icon">
                            <polyline points="3,6 5,6 21,6"/>
                            <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Seção de Comentários */}
                    {showComments[post.id] && (
                      <div className="orkut-comments-section">
                        {/* Lista de comentários */}
                        {post.comments && typeof post.comments === 'object' && Object.keys(post.comments).length > 0 ? (
                          Object.entries(post.comments).map(([commentId, comment]) => (
                          <div key={commentId} className="orkut-comment-item">
                            <div 
                              className="orkut-comment-avatar"
                              style={{
                                backgroundImage: `url(${comment.authorPhoto || DEFAULT_AVATAR})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            />
                            <div className="orkut-comment-content">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '2px' }}>
                                <span className="orkut-comment-author">{comment.authorName}</span>
                                {comment.authorVerified && (
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="#1DA1F2"
                                    className="verified-badge"
                                    title="Perfil Verificado"
                                    style={{ marginLeft: '2px' }}
                                  >
                                    <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
                                  </svg>
                                )}
                                {/* Botão de deletar - mostra se é autor do comentário ou dono do post */}
                                {user && (user.id === comment.authorId || user.id === post.authorId) && (
                                  <button
                                    onClick={() => handleDeleteComment(post.id, commentId, comment.authorId, post.authorId)}
                                    style={{
                                      marginLeft: 'auto',
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: '#666',
                                      padding: '2px 5px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                    title="Excluir comentário"
                                  >
                                    <svg 
                                      width="14" 
                                      height="14" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </button>
                                )}
                              </div>
                              <div className="orkut-comment-text">{comment.text}</div>
                              <div className="orkut-comment-time">{formatTime(comment.createdAt)}</div>
                            </div>
                          </div>
                          ))
                        ) : (
                          <div style={{ padding: '15px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                            Nenhum comentario -
                          </div>
                        )}

                        {/* Campo para adicionar comentário */}
                        <div className="orkut-add-comment">
                          <input
                            type="text"
                            placeholder="Escreva um comentário..."
                            value={commentText[post.id] || ""}
                            onChange={(e) => setCommentText({ ...commentText, [post.id]: e.target.value })}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleComment(post.id, commentText[post.id]);
                              }
                            }}
                            className="orkut-comment-input"
                          />
                          <button
                            onClick={() => handleComment(post.id, commentText[post.id])}
                            className="orkut-btn-comment"
                          >
                            Enviar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
          </div>
        </main>

        <aside className="orkut-sidebar-right">
          <div className="orkut-amigos">
            <strong>amigos ({friends.length})</strong>
            <div className="search-container">
              <input
                type="text"
                placeholder="buscar conexões"
                className="orkut-amigos-search"
                value={friendSearch}
                onChange={(e) => handleSearchChange(e.target.value, false)}
                onFocus={() => {
                  if (friendSearch.length >= 2) {
                    handleSearchChange(friendSearch, false);
                  }
                }}
                onBlur={() => {
                  // Delay para permitir clique nos resultados
                  setTimeout(() => setShowSearchDropdown(false), 300);
                }}
              />
            </div>
            <div className="orkut-amigos-list">
              {(() => {
                const query = (friendSearch || "").trim().toLowerCase();

                // Sem amigos adicionados
                if (!friends || friends.length === 0) {
                  return (
                    <p style={{ fontSize: '0.9em', color: '#666', padding: '20px' }}>
                      Comece adicionando amigos!
                    </p>
                  );
                }

                // Determina a lista a ser exibida (busca ou slice/complete)
                let listToShow = [];
                if (query) {
                  listToShow = friends.filter(f => {
                    const name = (f.nome || '').toLowerCase();
                    const email = (f.email || '').toLowerCase();
                    return name.includes(query) || email.includes(query);
                  });
                } else {
                  listToShow = showAllFriends ? friends : friends.slice(0, 6);
                }

                // Quando a busca não retorna resultados
                if (listToShow.length === 0) {
                  return (
                    <p style={{ fontSize: '0.9em', color: '#666', padding: '20px' }}>
                      Nenhum amigo encontrado.
                    </p>
                  );
                }

                // Renderiza itens
                return listToShow.map(friend => {
                  const displayName = friend.nome || "Amigo";
                  const shortName = displayName.length > 7 ? displayName.substring(0, 7) : displayName;

                  return (
                    <div 
                      key={friend.id} 
                      className="orkut-amigo-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNav("viewProfile", friend.id);
                      }}
                    >
                      <div 
                        className="orkut-amigo-avatar"
                        style={{
                          backgroundImage: `url(${friend.avatar || DEFAULT_AVATAR})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                      <div className="orkut-amigo-info">
                        <div className="orkut-amigo-nome" title={displayName}>{shortName}</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            {friends.length > 6 && (
              <div className="orkut-ver-mais-amigos">
                <button onClick={() => setShowAllFriends(!showAllFriends)}>
                  {showAllFriends ? '▲ ver menos' : `▼ ver todos (${friends.length})`}
                </button>
              </div>
            )}
          </div>

          {profileData?.universidade && (
            <div className="orkut-university-online">
              <strong>{getUniversityAcronym(profileData.universidade)} ({onlineUniversityUsers})</strong>
              <p style={{ 
                fontSize: '0.9em', 
                color: getUniversityColor(profileData.universidade), 
                padding: '10px 10px 5px',
                fontWeight: '600',
                margin: 0
              }}>
                {onlineUniversityUsers > 0 
                  ? `${onlineUniversityUsers} ${onlineUniversityUsers === 1 ? 'pessoa online' : 'pessoas online'}`
                  : 'Nenhum colega online'
                }
              </p>
              
              {/* Lista de usuários online com fotos */}
              {onlineUniversityUsersList.length > 0 && (
                <div style={{ 
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {onlineUniversityUsersList.map(onlineUser => (
                    <div 
                      key={onlineUser.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '6px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onClick={() => onNav("viewProfile", onlineUser.id)}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundImage: `url(${onlineUser.avatar || DEFAULT_AVATAR})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        flexShrink: 0,
                        border: '2px solid #4CAF50',
                        position: 'relative'
                      }}>
                        {/* Indicador verde de online */}
                        <div style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          width: '12px',
                          height: '12px',
                          background: '#4CAF50',
                          border: '2px solid white',
                          borderRadius: '50%'
                        }} />
                      </div>
                      <div style={{
                        flex: 1,
                        minWidth: 0
                      }}>
                        <div style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          color: '#333',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {onlineUser.nome}
                        </div>
                        <div style={{
                          fontSize: '0.75em',
                          color: '#4CAF50',
                          fontWeight: '500'
                        }}>
                          • online
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {onlineUniversityUsers > 3 && (
                    <div style={{
                      fontSize: '0.8em',
                      color: '#666',
                      textAlign: 'center',
                      marginTop: '5px',
                      fontStyle: 'italic'
                    }}>
                      +{onlineUniversityUsers - 3} {onlineUniversityUsers - 3 === 1 ? 'outro online' : 'outros online'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="orkut-artigos">
            <strong>revista científica</strong>
            <p style={{ fontSize: '0.9em', color: '#666', padding: '10px' }}>
              Compartilhe e leia artigos científicos da comunidade acadêmica!
            </p>
            <button 
              className="orkut-btn-secondary" 
              style={{ width: '100%', marginTop: '10px' }}
              onClick={() => onNav("revistas")}
            >
              explorar revista
            </button>
          </div>
        </aside>
      </div>

      {/* Modal Dia de Sorte */}
      {showLuckyDay && (
        <div className="lucky-day-overlay" onClick={closeLuckyDay}>
          <div className="lucky-day-modal" onClick={(e) => e.stopPropagation()}>
            <button className="lucky-day-close" onClick={closeLuckyDay}>✕</button>
            <div className="lucky-day-icon">🍀</div>
            <h2 className="lucky-day-title">Dia de Sorte!</h2>
            <p className="lucky-day-phrase">{luckyPhrase}</p>
            <p className="lucky-day-subtitle">Você foi sorteado(a) para receber essa mensagem especial! ✨</p>
          </div>
        </div>
      )}



      {/* Toast Notification (para outras coisas que não são chat/proximidade) */}
      {/* Toast de Notificação Profissional */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          photo={toast.photo}
          onClose={() => setToast(null)}
        />
      )}

      {/* Notificação de Mensagem em Tempo Real */}
      {messageNotification && (
        <div 
          onClick={() => {
            openChatToast(messageNotification.senderData);
            setMessageNotification(null);
          }}
          className="message-notification-desktop"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            width: '90%',
            maxWidth: '380px',
            zIndex: 9999,
            cursor: 'pointer',
            animation: 'slideUpFromBottom 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)';
          }}
        >
          {/* Avatar com Badge Online */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img 
              src={messageNotification.senderPhoto}
              alt={messageNotification.senderName}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #f0f0f0'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              width: '14px',
              height: '14px',
              backgroundColor: '#00b894',
              borderRadius: '50%',
              border: '2px solid #ffffff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}/>
          </div>

          {/* Conteúdo */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: '600', 
              fontSize: '14px',
              color: '#2d3436',
              marginBottom: '3px',
              letterSpacing: '-0.01em'
            }}>
              {messageNotification.senderName}
            </div>
            <div style={{ 
              fontSize: '13px',
              color: '#636e72',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.4'
            }}>
              {messageNotification.message}
            </div>
          </div>

          {/* Botão Fechar */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMessageNotification(null);
            }}
            style={{
              background: '#f1f3f5',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#868e96',
              padding: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e9ecef';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f1f3f5';
              e.currentTarget.style.color = '#868e96';
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Modal de Confirmação Profissional */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmType={confirmModal.confirmType}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}

      {/* Modal de Solicitação de Conexão */}
      {connectionRequest && (
        <div className="connection-request-overlay">
          <div className="connection-request-modal">
            <div className="connection-request-content">
              <div className="connection-request-avatar">
                <img 
                  src={connectionRequest.avatar || DEFAULT_AVATAR} 
                  alt={connectionRequest.name}
                />
              </div>
              <h3>Deseja se conectar com:</h3>
              <p className="connection-request-name">{connectionRequest.name}</p>
              <p className="connection-request-distance">
                {connectionRequest.distance ? `${Math.round(connectionRequest.distance)}m de distância` : 'Usuário próximo'}
              </p>
              <div className="connection-request-buttons">
                <button 
                  className="connection-btn connection-btn-no"
                  onClick={() => handleConnectionResponse(false)}
                >
                  Não
                </button>
                <button 
                  className="connection-btn connection-btn-yes"
                  onClick={() => handleConnectionResponse(true)}
                >
                  Sim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Barra Mobile */}
      <div className="orkut-mobile-nav">
        {!mobileMenuExpanded ? (
          <>
            <button className="orkut-mobile-nav-btn" onClick={() => window.scrollTo(0, 0)}>
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
              <span>Home</span>
            </button>
            <button className="orkut-mobile-nav-btn" onClick={() => onNav("profile")}>
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <span>Perfil</span>
            </button>
            <button className="orkut-mobile-nav-btn" onClick={() => onNav("friends")}>
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              <span>Amigos</span>
            </button>
            <button className="orkut-mobile-nav-btn orkut-mobile-expand" onClick={() => setMobileMenuExpanded(true)}>
              <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
            </button>
          </>
        ) : (
          <>
            <button className="orkut-mobile-nav-btn" onClick={() => onNav("revistas")}>
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              <span>Revista</span>
            </button>
            <button className="orkut-mobile-nav-btn" onClick={() => onNav("startups")}>
              <svg viewBox="0 0 24 24" fill="white" width="24" height="24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span>StartUps</span>
            </button>
            <button className="orkut-mobile-nav-btn orkut-mobile-expand" onClick={() => setMobileMenuExpanded(false)}>
              <svg viewBox="0 0 24 24" fill="white" width="28" height="28">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Chat Toast */}
      {activeChatToast && (
        <ChatToast
          friendData={activeChatToast}
          onClose={() => setActiveChatToast(null)}
          onExpand={() => {
            // Quando expandir, pode implementar lógica adicional se necessário
          }}
        />
      )}

      {/* Image Cropper Modal */}
      {showImageCropper && imageToCrop && (
        <ImageCropperModal
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Painel Administrativo */}
      {showAdminPanel && (
        <AdminPanel 
          currentUser={user}
          onClose={() => setShowAdminPanel(false)}
        />
      )}

      {/* Debug temporário - descomentar se precisar testar
      <DatabaseDebug user={user} />
      */}
    </div>
  );
}

// Componente de Cropper de Imagem
function ImageCropperModal({ image, onCropComplete, onCancel }) {
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [zoom, setZoom] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imgRef = React.useRef(new Image());
  const [imgDimensions, setImgDimensions] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const img = imgRef.current;
    img.onload = () => {
      setImgDimensions({ width: img.width, height: img.height });
      setImageLoaded(true);
      
      // Calcular zoom inicial para a imagem preencher bem a área de corte
      const cropSize = 600; // Área de corte fixa
      const minDimension = Math.min(img.width, img.height);
      
      // Se a imagem for muito grande, ajustar zoom para caber melhor
      // Queremos que a menor dimensão fique um pouco maior que a área de corte
      const targetSize = cropSize * 1.2; // 20% maior que a área de corte
      const initialZoom = targetSize / minDimension;
      
      // Limitar zoom entre 0.5 e 2 para não ficar muito pequeno nem muito grande
      setZoom(Math.min(Math.max(initialZoom, 0.5), 2));
    };
    img.src = image;
  }, [image]);

  React.useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [zoom, position, imageLoaded]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const img = imgRef.current;
    if (!canvas || !img.complete || !container) return;

    const ctx = canvas.getContext('2d');
    const cropSize = 600;
    
    // Tamanho da imagem escalada
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    
    // Posição centralizada + offset do drag
    const imgX = (canvas.width - scaledWidth) / 2 + position.x;
    const imgY = (canvas.height - scaledHeight) / 2 + position.y;
    
    // Limpar canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Desenhar imagem
    ctx.drawImage(img, imgX, imgY, scaledWidth, scaledHeight);
    
    // Desenhar overlay escuro
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Área de corte no centro (sempre fixa)
    const cropX = (canvas.width - cropSize) / 2;
    const cropY = (canvas.height - cropSize) / 2;
    
    // Redesenhar a parte da imagem na área de corte
    ctx.save();
    ctx.beginPath();
    ctx.rect(cropX, cropY, cropSize, cropSize);
    ctx.clip();
    ctx.drawImage(img, imgX, imgY, scaledWidth, scaledHeight);
    ctx.restore();
    
    // Desenhar borda da área de corte
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.strokeRect(cropX, cropY, cropSize, cropSize);
    
    // Desenhar linhas de grade (terços)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    const third = cropSize / 3;
    // Linhas verticais
    ctx.beginPath();
    ctx.moveTo(cropX + third, cropY);
    ctx.lineTo(cropX + third, cropY + cropSize);
    ctx.moveTo(cropX + third * 2, cropY);
    ctx.lineTo(cropX + third * 2, cropY + cropSize);
    // Linhas horizontais
    ctx.moveTo(cropX, cropY + third);
    ctx.lineTo(cropX + cropSize, cropY + third);
    ctx.moveTo(cropX, cropY + third * 2);
    ctx.lineTo(cropX + cropSize, cropY + third * 2);
    ctx.stroke();
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoomChange = (newZoom) => {
    setZoom(Math.max(0.5, Math.min(3, newZoom)));
  };

  const handleCrop = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img.complete) return;
    
    const cropSize = 600;
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    
    // Posição da imagem no canvas
    const imgX = (canvas.width - scaledWidth) / 2 + position.x;
    const imgY = (canvas.height - scaledHeight) / 2 + position.y;
    
    // Posição da área de corte
    const cropX = (canvas.width - cropSize) / 2;
    const cropY = (canvas.height - cropSize) / 2;
    
    // Calcular qual parte da imagem original corresponde à área de corte
    const sourceX = (cropX - imgX) / zoom;
    const sourceY = (cropY - imgY) / zoom;
    const sourceSize = cropSize / zoom;
    
    // Criar canvas temporário para o corte final
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    // Definir tamanho do canvas para 450x450 (tamanho final do post)
    tempCanvas.width = 450;
    tempCanvas.height = 450;
    
    // Desenhar a área cortada
    tempCtx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      450,
      450
    );
    
    // Converter para base64 com compressão
    const croppedImage = tempCanvas.toDataURL('image/jpeg', 0.85);
    onCropComplete(croppedImage);
  };

  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, dragStart]);

  return (
    <div className="orkut-modal-bg" onClick={onCancel}>
      <div className="orkut-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
        <div className="orkut-modal-header">
          <h2>Ajustar Imagem</h2>
          <button className="orkut-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="orkut-modal-content">
          <p style={{ textAlign: 'center', marginBottom: '12px', color: '#666', fontSize: '14px' }}>
            Arraste para mover • Use o controle para dar zoom • A imagem será cortada em 1:1
          </p>
          
          <div 
            ref={containerRef}
            style={{ 
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
              userSelect: 'none'
            }}
          >
            <canvas
              ref={canvasRef}
              width={700}
              height={700}
              style={{
                width: '100%',
                maxWidth: '700px',
                border: '2px solid #ddd',
                borderRadius: '8px',
                display: 'block',
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none'
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          </div>

          {/* Controle de Zoom */}
          <div style={{ marginBottom: '16px', padding: '0 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                onClick={() => handleZoomChange(zoom - 0.1)}
                style={{
                  background: '#003366',
                  color: '#fff',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                -
              </button>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <button 
                onClick={() => handleZoomChange(zoom + 0.1)}
                style={{
                  background: '#003366',
                  color: '#fff',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                +
              </button>
              <span style={{ minWidth: '50px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
                {Math.round(zoom * 100)}%
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="orkut-btn-primary" onClick={handleCrop} style={{ minWidth: '140px' }}>
              Confirmar Corte
            </button>
            <button className="orkut-btn-secondary" onClick={onCancel} style={{ minWidth: '100px' }}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;